/**
 * The page's one sound engine for the reel openers (spinner and battle), from `@loot-labs/sound`,
 * which boxed-frontend plays its sounds through too. Openers fire the shared sound events below;
 * what each sounds like is content: a host's sound files are a layer of this engine.
 *
 * Every opener on a page shares the engine and its one AudioContext. When two mounted openers
 * bring different files, the one mounted last answers; its unmount hands back to the other.
 */

import {
  createSoundEngine,
  type SoundEngine,
  type SoundEngineOptions,
  type SoundEventSpec,
  type SoundManifest,
} from "@loot-labs/sound";
import {
  SHOPPER_SOUND_VOLUME_DEFAULT,
  SOUND_EVENTS,
  shopperVolumeGain,
} from "@loot-os/api-contract";

/** Every sound event the openers fire. Mirrors api-contract's `SoundEventKey` (a test pins it). */
export type OpenerSoundEvent =
  | "opener.spinner.tick"
  | "opener.reveal.bad"
  | "opener.reveal.rare"
  | "opener.reveal.mega"
  | "battle.spinner.tick"
  | "battle.reveal.bad"
  | "battle.reveal.rare"
  | "battle.reveal.mega";

/**
 * The events the reel openers fire. Keys, descriptions and fallbacks come from api-contract's
 * `SOUND_EVENTS` (the list the dashboard shows); the engine-only fields stay local.
 */
export const OPENER_SOUND_EVENTS: Record<OpenerSoundEvent, SoundEventSpec> = Object.fromEntries(
  SOUND_EVENTS.map((event) => [
    event.key,
    {
      description: event.description,
      category: event.group,
      bus: "sfx",
      group: event.key.startsWith("battle.") ? "battle" : "opener",
      ...(event.fallback ? { fallback: event.fallback } : {}),
      ...(event.key.endsWith(".spinner.tick") ? { late: "drop" } : {}),
    } satisfies SoundEventSpec,
  ]),
) as Record<OpenerSoundEvent, SoundEventSpec>;

type EngineOptions = Omit<SoundEngineOptions, "catalog">;

let engine: SoundEngine | null = null;
let engineOptions: EngineOptions = {};
/** Host layers, the most recently added last. */
const hostLayers: { key: symbol; manifest: SoundManifest }[] = [];

/** One mounted source of the shopper's level (a provider, or an opener given the props itself). */
export interface ShopperSoundLevelInput {
  muted?: boolean;
  /** 0 to 100. A non-finite value counts as the default. */
  volume?: number;
}

/** The level the engine plays at, as the channel reports it. */
interface ShopperSoundLevel {
  muted: boolean;
  gain: number;
}

const FULL_LEVEL: ShopperSoundLevel = { muted: false, gain: 1 };
interface LevelEntry {
  source: ShopperSoundLevelSource;
  level: ShopperSoundLevel | null;
}
const levelInputs: LevelEntry[] = [];
const levelListeners = new Set<() => void>();
let currentLevel: ShopperSoundLevel = FULL_LEVEL;

/** The shopper's level now: the most recently mounted source answers, else full volume. */
export function shopperSoundLevel(): Readonly<ShopperSoundLevel> {
  return currentLevel;
}

/** Calls the listener whenever the shopper's mute or volume changes; returns the unsubscribe. */
export function subscribeShopperSoundLevel(listener: () => void): () => void {
  levelListeners.add(listener);
  return () => {
    levelListeners.delete(listener);
  };
}

function applyLevel(): void {
  const held = levelInputs.filter((entry) => entry.level !== null);
  const winner = held.findLast((entry) => entry.source === "opener") ?? held[held.length - 1];
  const top = winner?.level ?? FULL_LEVEL;
  const changed = top.muted !== currentLevel.muted || top.gain !== currentLevel.gain;
  currentLevel = top;
  engine?.setMuted(top.muted);
  engine?.setMasterVolume(top.gain);
  if (changed) for (const listener of [...levelListeners]) listener();
}

/** Where a level comes from: an opener's own props beat the provider's, whatever mounted first. */
export type ShopperSoundLevelSource = "provider" | "opener";

/** A mounted hook's slot in the level stack; `set(null)` leaves it in place but silent about level. */
export interface ShopperSoundLevelHandle {
  set: (input: ShopperSoundLevelInput | null) => void;
  remove: () => void;
}

function toLevel(input: ShopperSoundLevelInput): ShopperSoundLevel {
  const volume = Number.isFinite(input.volume)
    ? (input.volume as number)
    : SHOPPER_SOUND_VOLUME_DEFAULT;
  return {
    muted: input.muted === true || volume === 0,
    // An absent volume is the full level of a page with no provider.
    gain: input.volume === undefined ? 1 : shopperVolumeGain(volume),
  };
}

/**
 * Takes a slot for the shopper's mute and volume (0 to 100). Among the slots that hold a level,
 * `opener` beats `provider`, and within a source the most recently taken wins. Updating a slot in
 * place never drops to the level below. A volume of 0 mutes. The level is held here and applied
 * when the engine is created, so setting it never makes an AudioContext.
 */
export function addShopperSoundLevel(source: ShopperSoundLevelSource): ShopperSoundLevelHandle {
  const entry: LevelEntry = { source, level: null };
  levelInputs.push(entry);
  return {
    set: (input) => {
      entry.level = input ? toLevel(input) : null;
      applyLevel();
    },
    remove: () => {
      const index = levelInputs.indexOf(entry);
      if (index === -1) return;
      levelInputs.splice(index, 1);
      applyLevel();
    },
  };
}

let removeGestureListeners: (() => void) | null = null;

/** The first tap or key anywhere on the page unlocks audio and loads every event's file. */
function listenForFirstGesture(created: SoundEngine): void {
  const onGesture = () => {
    removeGestureListeners?.();
    created.unlock();
    created.preload(Object.keys(OPENER_SOUND_EVENTS));
  };
  document.addEventListener("pointerdown", onGesture, true);
  document.addEventListener("keydown", onGesture, true);
  removeGestureListeners = () => {
    document.removeEventListener("pointerdown", onGesture, true);
    document.removeEventListener("keydown", onGesture, true);
    removeGestureListeners = null;
  };
}

function syncLayers(): void {
  engine?.setLayers(
    [...hostLayers]
      .reverse()
      .map((layer, index) => ({ name: `host-${index}`, manifest: layer.manifest })),
  );
}

/** Created on first use, so a page whose openers never sound never makes an AudioContext. */
export function openerSoundEngine(): SoundEngine {
  if (engine) return engine;
  const created = createSoundEngine({ catalog: OPENER_SOUND_EVENTS, ...engineOptions });
  engine = created;
  syncLayers();
  created.setMuted(currentLevel.muted);
  created.setMasterVolume(currentLevel.gain);
  if (typeof document !== "undefined") {
    listenForFirstGesture(created);
    // Keeps playing while hidden; on return the engine resumes a context the browser suspended.
    document.addEventListener("visibilitychange", () => created.setHidden(document.hidden));
  }
  return created;
}

/** Adds a host's sounds as the top layer; the returned function removes them. */
export function addHostSoundLayer(manifest: SoundManifest): () => void {
  const key = Symbol("host sounds");
  hostLayers.push({ key, manifest });
  syncLayers();
  return () => {
    const index = hostLayers.findIndex((layer) => layer.key === key);
    if (index === -1) return;
    hostLayers.splice(index, 1);
    syncLayers();
  };
}

/** Tests: drop the engine and every layer, and build the next engine with these options. */
export function resetOpenerSoundEngine(options: EngineOptions = {}): void {
  engine = null;
  engineOptions = options;
  hostLayers.length = 0;
  removeGestureListeners?.();
  levelInputs.length = 0;
  levelListeners.clear();
  currentLevel = FULL_LEVEL;
}
